import React, { useState, useEffect } from "react";
import { View, TouchableOpacity, Image, ScrollView } from "react-native";
import { Text } from "react-native-paper";
import { Controller, Control, FieldError } from "react-hook-form";
import Button from "../Button";
import { cloudinaryConfig } from "../../config/cloudinary";

declare global {
  interface Window {
    cloudinary: {
      createUploadWidget: (
        options: any,
        callback: (error: any, result: any) => void
      ) => {
        open: () => void;
        close: () => void;
        destroy: () => void;
      };
    };
  }
}

interface CloudinaryResult {
  event: string;
  info: {
    secure_url: string;
    public_id: string;
    format: string;
    width: number;
    height: number;
    bytes: number;
    resource_type: string;
    created_at: string;
    thumbnail_url: string;
    url: string;
  };
}

interface ImageUploadProps {
  control: Control<any>;
  name: string;
  label: string;
  multiple?: boolean;
  maxImages?: number;
  required?: boolean;
  disabled?: boolean;
}

interface ImageSelectionProps {
  images: CloudinaryResult["info"][];
  onAddImage: () => void;
  onRemoveImage: (index: number) => void;
  multiple?: boolean;
  maxImages?: number;
  disabled?: boolean;
  error?: FieldError;
  label: string;
}

const ImageSelection: React.FC<ImageSelectionProps> = ({
  images,
  onAddImage,
  onRemoveImage,
  multiple = false,
  maxImages = 5,
  disabled = false,
  error,
  label,
}) => {
  const canAddMore = multiple ? images.length < maxImages : images.length === 0;

  return (
    <View className="mb-4">
      <Text className="text-regular-tight font-600 text-ink-darkest mb-3">{label}</Text>

      {images.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} className="mb-3">
          <View className="flex-row space-x-2" style={{ paddingTop: 8, paddingRight: 8 }}>
            {images.map((image, index) => (
              <View key={index} className="relative" style={{ marginRight: 8 }}>
                <Image
                  source={{ uri: image.thumbnail_url || image.secure_url }}
                  className="w-20 h-20 rounded-lg"
                  style={{ width: 80, height: 80 }}
                  resizeMode="cover"
                />
                {!disabled && (
                  <TouchableOpacity
                    onPress={() => onRemoveImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full w-6 h-6 items-center justify-center"
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      backgroundColor: "#ef4444",
                      borderRadius: 12,
                      width: 24,
                      height: 24,
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      elevation: 10, // For Android shadow
                    }}
                  >
                    <Text className="text-white text-xs font-bold">×</Text>
                  </TouchableOpacity>
                )}
              </View>
            ))}
          </View>
        </ScrollView>
      )}

      {canAddMore && !disabled && (
        <View className="flex-row space-x-2">
          <Button mode="outlined" onPress={onAddImage} icon="upload" iconPosition="left" className="flex-1" style={{ flex: 1 }}>
            {images.length === 0 ? `Choose ${label}` : `Add Another`}
          </Button>
        </View>
      )}

      {error && <Text className="text-red-600 text-sm mt-1">{error.message}</Text>}

      {multiple && (
        <Text className="text-ink-darker text-xs mt-1">
          {images.length}/{maxImages} images selected
        </Text>
      )}

      <Text className="text-ink-darker text-xs mt-1">Supported formats: JPG, PNG, WebP (max 10MB each)</Text>
    </View>
  );
};

export const ImageUpload: React.FC<ImageUploadProps> = ({
  control,
  name,
  label,
  multiple = false,
  maxImages = 5,
  required = false,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [widget, setWidget] = useState<any>(null);

  useEffect(() => {
    // Load Cloudinary Upload Widget script
    const script = document.createElement("script");
    script.src = "https://upload-widget.cloudinary.com/latest/global/all.js";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      if (widget) {
        widget.destroy();
      }
      document.head.removeChild(script);
    };
  }, []);

  const openUploadWidget = (
    currentImages: CloudinaryResult["info"][],
    onChange: (images: CloudinaryResult["info"][]) => void
  ) => {
    if (!window.cloudinary) {
      console.error("Cloudinary Upload Widget not loaded");
      return;
    }

    setIsLoading(true);

    const maxFilesToUpload = multiple ? maxImages - currentImages.length : 1;

    const uploadWidget = window.cloudinary.createUploadWidget(
      {
        cloudName: cloudinaryConfig.cloudName,
        uploadPreset: cloudinaryConfig.uploadPreset,
        sources: ["local", "url", "camera"],
        multiple: multiple,
        maxFiles: maxFilesToUpload,
        folder: "hole-in-one",
        resourceType: "image",
        clientAllowedFormats: ["jpg", "jpeg", "png", "webp"],
        maxFileSize: 10000000, // 10MB
        showAdvancedOptions: false,
        cropping: false,
        showSkipCropButton: true,
        croppingAspectRatio: multiple ? undefined : 1,
        theme: "minimal",
        styles: {
          palette: {
            window: "#FFFFFF",
            windowBorder: "#E5E7EB",
            tabIcon: "#6B7280",
            menuIcons: "#6B7280",
            textDark: "#1F2937",
            textLight: "#6B7280",
            link: "#3B82F6",
            action: "#3B82F6",
            inactiveTabIcon: "#9CA3AF",
            error: "#EF4444",
            inProgress: "#F59E0B",
            complete: "#10B981",
            sourceBg: "#F9FAFB",
          },
        },
      },
      (error: any, result: CloudinaryResult) => {
        setIsLoading(false);

        if (error) {
          console.error("Upload Widget Error:", error);
          return;
        }

        if (result.event === "success") {
          console.log("Upload successful:", result.info);

          if (multiple) {
            const newImages = [...currentImages, result.info];
            onChange(newImages);
          } else {
            onChange([result.info]);
          }
        }

        if (result.event === "close") {
          console.log("Upload widget closed");
        }
      }
    );

    setWidget(uploadWidget);
    uploadWidget.open();
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? `${label} is required` : undefined,
      }}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => (
        <ImageSelection
          images={value}
          onAddImage={() => openUploadWidget(value, onChange)}
          onRemoveImage={(index) => {
            const newImages = [...value];
            newImages.splice(index, 1);
            onChange(newImages);
          }}
          multiple={multiple}
          maxImages={maxImages}
          disabled={disabled || isLoading}
          error={error}
          label={label}
        />
      )}
    />
  );
};

export default ImageUpload;
